/**
 * FE-20: Integrations Page — Tab-based view of SDK / OpenClaw / Plugin metrics.
 * Each tab: KPI cards + version distribution table.
 */

import { useCallback, useEffect, useState } from "react";
import { fetchIntegrationMetrics } from "../../api/admin-integrations";
import type {
  IntegrationDetail,
  IntegrationTypeKey,
} from "../../api/admin-integrations";
import { Td, Th, tableStyles } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";
import { fmt, pct } from "../metrics/helpers";

// ─── Tab Config ───

const TABS = [
  { key: "sdk" as const, label: "integrations.tab_sdk" },
  { key: "openclaw" as const, label: "integrations.tab_openclaw" },
  { key: "claude_code" as const, label: "integrations.tab_claude_code" },
  { key: "codex" as const, label: "integrations.tab_codex" },
  { key: "cli" as const, label: "integrations.tab_cli" },
] as const;

// ─── Main Component ───

export function IntegrationsPage(): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<IntegrationTypeKey>("sdk");
  const { data, error, loading, refresh } = useIntegrationData(activeTab);

  return (
    <div>
      <PageHeader
        title={t("integrations.title")}
        description={t("integrations.description")}
        actions={
          <button type="button" onClick={refresh} style={shared.refreshBtn}>
            {t("common.refresh")}
          </button>
        }
      />
      <IntegrationTabs active={activeTab} onSelect={setActiveTab} />
      <div style={s.content}>
        {error && <p style={shared.errorBanner}>{error}</p>}
        {loading && !data && (
          <p style={shared.loadingText}>{t("common.loading")}</p>
        )}
        {data && <MetricsCards data={data} />}
        {data && <VersionTable versions={data.versions} />}
      </div>
    </div>
  );
}

// ─── Tab Bar ───

function IntegrationTabs({
  active,
  onSelect,
}: {
  active: IntegrationTypeKey;
  onSelect: (tab: IntegrationTypeKey) => void;
}): React.JSX.Element {
  return (
    <div style={s.tabBar}>
      {TABS.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onSelect(tab.key)}
          style={tab.key === active ? { ...s.tab, ...s.tabActive } : s.tab}
        >
          {t(tab.label)}
        </button>
      ))}
    </div>
  );
}

// ─── KPI Cards ───

function MetricsCards({
  data,
}: {
  data: IntegrationDetail;
}): React.JSX.Element {
  const { metrics } = data;
  return (
    <section style={shared.section}>
      <div style={shared.cardGrid}>
        <KPICard
          label={t("integrations.active_accounts")}
          value={fmt(metrics.active_accounts)}
        />
        <KPICard
          label={t("integrations.active_agents")}
          value={fmt(metrics.active_agents)}
        />
        <KPICard
          label={t("integrations.messages")}
          value={fmt(metrics.messages)}
        />
        <KPICard
          label={t("integrations.deliveries")}
          value={fmt(metrics.deliveries)}
        />
        <KPICard
          label={t("integrations.errors")}
          value={fmt(metrics.errors)}
          alert={metrics.errors > 0}
        />
        <KPICard
          label={t("integrations.disconnects")}
          value={fmt(metrics.disconnects)}
          alert={metrics.disconnects > 10}
        />
      </div>
    </section>
  );
}

function KPICard({
  label,
  value,
  alert,
}: {
  label: string;
  value: string;
  alert?: boolean;
}): React.JSX.Element {
  return (
    <div style={{ ...shared.card, padding: 20 }}>
      <span style={s.cardLabel}>{label}</span>
      <span
        style={{
          ...shared.bigNumber,
          color: alert ? "#dc2626" : "var(--color-text)",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Version Table ───

function VersionTable({
  versions,
}: {
  versions: ReadonlyArray<IntegrationDetail["versions"][number]>;
}): React.JSX.Element {
  return (
    <section style={shared.section}>
      <h3 style={shared.sectionTitle}>
        {t("integrations.version_distribution")}
      </h3>
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <Th>{t("integrations.col_version")}</Th>
            <Th>{t("integrations.col_count")}</Th>
            <Th>{t("integrations.col_share")}</Th>
          </tr>
        </thead>
        <tbody>
          {versions.map((row) => (
            <tr key={row.version}>
              <Td mono>{row.version}</Td>
              <Td mono>{fmt(row.count)}</Td>
              <Td>{pct(row.percentage)}</Td>
            </tr>
          ))}
          {versions.length === 0 && (
            <tr>
              <Td colSpan={3}>{t("integrations.no_version_data")}</Td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

// ─── Data Hook ───

function useIntegrationData(tab: IntegrationTypeKey): {
  data: IntegrationDetail | null;
  error: string | null;
  loading: boolean;
  refresh: () => void;
} {
  const [data, setData] = useState<IntegrationDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchIntegrationMetrics({ integration_type: tab }));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, error, loading, refresh: load };
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  tabBar: {
    display: "flex",
    gap: 0,
    borderBottom: "1px solid var(--color-border)",
    marginBottom: 24,
  },
  tab: {
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    borderBottom: "2px solid transparent",
    marginBottom: -1,
    transition: "color 0.15s, border-color 0.15s",
  },
  tabActive: {
    color: "var(--color-text)",
    fontWeight: 600,
    borderBottomColor: "var(--color-primary)",
  },
  content: {
    minHeight: 200,
  },
  cardLabel: {
    display: "block",
    fontSize: 13,
    color: "var(--color-text-secondary)",
    fontWeight: 500,
    marginBottom: 4,
  },
};
